import React from 'react';
import { Package, Trash2, Building, FileText, User, ChevronDown, Settings, Activity, Briefcase, Type, Copy, Paperclip, ShoppingBag, Star , Network } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatGermanPrice } from 'core/utils/formatters';
import { formatQuantity } from 'core/utils/units';
import BanfTypeSelector from './BanfTypeSelector';
import GeneralTab from './GeneralTab';
import LimitsTab from './LimitsTab';
import ServicesTab from './ServicesTab';
import TextsTab from './TextsTab';
import AttachmentsTab from './AttachmentsTab';
import SourceTab from './SourceTab';

const BanfItemCard = ({
  item,
  onRemove,
  onCopy,
  onAddToFavorite,
  onUpdate,
  canRemove,
  selectedType,
  onTypeChange,
  costCenters,
  units,
  productGroups,
  plants,
  isExpanded,
  onToggleExpansion,
  activeTab,
  onTabChange,
  costCenterName,
  plantName,
  purchasingOrgName,
  purchasingGroupName,
  user,
  validationErrors
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Calculate total price from services
  const calculateServicesTotal = () => {
    if (!item.services || item.services.length === 0) return 0;
    return item.services.reduce((total, service) => {
      const quantity = parseFloat(service.service_quantity) || 0;
      const price = parseFloat(service.service_gross_price) || 0;
      return total + (quantity * price);
    }, 0);
  };
  
  // Check if any limits field is filled
  const hasLimitsData = () => {
    return (
      (item.total_limit && item.total_limit.trim() !== '') ||
      (item.expected_value && item.expected_value.toString().trim() !== '') ||
      (item.limits_gl_account && item.limits_gl_account.trim() !== '') ||
      (item.limits_cost_center && item.limits_cost_center.trim() !== '')
    );
  };

  // Check if services exist
  const hasServices = () => {
    return item.services && item.services.length > 0;
  };

  // Check if Kostenstelle should be hidden (either limits data exists or services exist)
  const shouldHideKostenstelle = () => {
    return hasLimitsData() || hasServices();
  };

  // Calculate effective price considering expected_value, services and price field
  const calculateEffectivePrice = () => {
    // If expected_value is set, it takes precedence
    if (item.expected_value && parseFloat(item.expected_value) > 0) {
      const expectedValue = parseFloat(item.expected_value) || 0;
      // Add services total if they exist
      if (item.services && item.services.length > 0) {
        return expectedValue + calculateServicesTotal();
      }
      return expectedValue;
    }
    // Otherwise use services total if services exist
    if (item.services && item.services.length > 0) {
      return calculateServicesTotal();
    }
    // Otherwise return the manual price, adjusted by price unit (PEINH):
    // effektiver Stückpreis = Preis / PEINH (z. B. 150,78 € pro PEINH 1000 -> 0,15078 €/ST)
    return (parseFloat(item.price) || 0) / Math.max(1, parseInt(item.price_unit, 10) || 1);
  };

  // Calculate position value based on services or estimated price
  const calculatePositionValue = () => {
    // Use the effective price (which considers expected_value, services, and price)
    const effectivePrice = calculateEffectivePrice();
    const quantity = parseFloat(item.quantity) || 1;
    
    // If we have services, return the effective price directly (already includes service totals)
    if (item.services && item.services.length > 0) {
      return effectivePrice;
    }
    
    // Otherwise multiply by quantity
    return effectivePrice * quantity;
  };

  // Theme colors based on selected type and whether services exist
  const getThemeColors = () => {
    const hasServices = item.services && item.services.length > 0;
    
    switch (selectedType) {
      case 'kostenstelle':
        // If Kostenstelle has services, use orange theme
        if (hasServices) {
          return {
            cardBg: isDarkMode
              ? 'bg-gradient-to-br from-gray-900/30 via-orange-900/10 to-gray-900/30 border border-orange-400/20'
              : 'bg-white border border-slate-200',
            cardHover: isDarkMode
              ? 'bg-gradient-to-t from-orange-900/10 via-transparent to-orange-800/20'
              : '',
            iconBg: 'from-orange-600/60 via-orange-700/50 to-orange-800/60',
            iconShadow: 'shadow-[0_8px_32px_rgba(249,115,22,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
            focusRing: 'focus:ring-orange-400/50'
          };
        }
        // Default green for Kostenstelle without services
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-green-900/10 to-gray-900/30 border border-green-400/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-green-900/10 via-transparent to-green-800/20'
            : '',
          iconBg: 'from-green-600/60 via-green-700/50 to-green-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(16,185,129,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-green-400/50'
        };
      case 'auftrag':
        // If Auftrag has services, change color to orange
        if (hasServices) {
          return {
            cardBg: isDarkMode
              ? 'bg-gradient-to-br from-gray-900/30 via-orange-900/10 to-gray-900/30 border border-orange-400/20'
              : 'bg-white border border-slate-200',
            cardHover: isDarkMode
              ? 'bg-gradient-to-t from-orange-900/10 via-transparent to-orange-800/20'
              : '',
            iconBg: 'from-orange-600/60 via-orange-700/50 to-orange-800/60',
            iconShadow: 'shadow-[0_8px_32px_rgba(249,115,22,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
            focusRing: 'focus:ring-orange-400/50'
          };
        }
        // Default purple for Auftrag without services
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-purple-900/10 to-gray-900/30 border border-purple-400/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-purple-900/10 via-transparent to-purple-800/20'
            : '',
          iconBg: 'from-purple-600/60 via-purple-700/50 to-purple-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-purple-400/50'
        };
      case 'dienstleistung':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-orange-900/10 to-gray-900/30 border border-orange-400/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-orange-900/10 via-transparent to-orange-800/20'
            : '',
          iconBg: 'from-orange-600/60 via-orange-700/50 to-orange-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(249,115,22,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-orange-400/50'
        };
      case 'psp':
        // PSP-Element: Amber — passend zum Typ-Button im BanfTypeSelector (Fix 28.07.:
        // psp fehlte im Switch und fiel auf den Default -> Button amber, Karte/Tabs fremdfarbig)
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-amber-900/10 to-gray-900/30 border border-amber-400/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-amber-900/10 via-transparent to-amber-800/20'
            : '',
          iconBg: 'from-amber-600/60 via-amber-700/50 to-amber-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-amber-400/50'
        };
      case 'material':
        // Material with services - use orange theme (consistent with other types)
        if (hasServices) {
          return {
            cardBg: isDarkMode
              ? 'bg-gradient-to-br from-gray-900/30 via-orange-900/10 to-gray-900/30 border border-orange-400/20'
              : 'bg-white border border-slate-200',
            cardHover: isDarkMode
              ? 'bg-gradient-to-t from-orange-900/10 via-transparent to-orange-800/20'
              : '',
            iconBg: 'from-orange-600/60 via-orange-700/50 to-orange-800/60',
            iconShadow: 'shadow-[0_8px_32px_rgba(249,115,22,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
            focusRing: 'focus:ring-orange-400/50'
          };
        }
        // Default blue for Material without services
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-blue-900/10 to-gray-900/30 border border-blue-400/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-blue-900/10 via-transparent to-blue-800/20'
            : '',
          iconBg: 'from-blue-600/60 via-blue-700/50 to-blue-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-blue-400/50'
        };
      default:
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
            : 'bg-white border border-slate-200',
          cardHover: isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : '',
          iconBg: 'from-purple-600/60 via-purple-700/50 to-purple-800/60',
          iconShadow: 'shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]',
          focusRing: 'focus:ring-purple-400/50'
        };
    }
  };

  const themeColors = getThemeColors();

  // Get icon based on selected type
  const getTypeIcon = () => {
    switch (selectedType) {
      case 'kostenstelle':
        return Building;
      case 'auftrag':
        return FileText;
      case 'dienstleistung':
        return User;
      case 'psp':
        return Network;
      case 'material':
        return Package;
      default:
        return Package;
    }
  };

  const TypeIcon = getTypeIcon();

  return (
    <div className="relative group/item">
      {/* Card Background with Theme Colors */}
      <div className={`absolute inset-0 ${themeColors.cardBg} ${isDarkMode ? 'backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] group-hover/item:shadow-[0_12px_40px_rgba(0,0,0,0.6)]' : 'shadow-sm group-hover/item:shadow-md'} rounded-3xl transition-all duration-300`}></div>
      <div className={`absolute inset-0 ${themeColors.cardHover} rounded-3xl opacity-0 group-hover/item:opacity-100 transition-opacity duration-300`}></div>
      
      <div className={`relative ${isExpanded ? 'p-6' : 'p-4'}`}>
        <div className={`flex items-center justify-between gap-4 ${isExpanded ? 'mb-6' : 'mb-0'}`}>
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Package Icon with Theme Colors */}
            <div className="relative group/packageicon">
              <div className={`absolute inset-0 rounded-2xl blur-sm ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent' : 'hidden'}`}></div>
              <div className={`relative bg-gradient-to-br ${themeColors.iconBg} ${isDarkMode ? 'backdrop-blur-xl' : ''} w-10 h-10 rounded-2xl flex items-center justify-center ${themeColors.iconShadow} border border-white/20`}>
                <TypeIcon className="w-5 h-5 text-white/90 drop-shadow-lg" />
                <div className={`absolute top-1 left-1 w-2.5 h-2.5 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>
            <button
              onClick={() => onToggleExpansion(item.id)}
              className="flex items-center space-x-3 group/header hover:opacity-80 transition-opacity duration-200 min-w-0 flex-1 max-w-xl"
            >
              <div className="min-w-0 flex-1">
                <h3 className={`text-lg font-semibold line-clamp-2 break-words ${isDarkMode ? 'text-white' : 'text-gray-800'} drop-shadow-lg text-left`} title={item.description || t('banf.positionLabel', { n: item.position_number })}>
                  {item.description ? item.description : t('banf.positionLabel', { n: item.position_number })}
                </h3>
                {!isExpanded && !item.description && (
                  <p className={`${isDarkMode ? 'text-white/70' : 'text-gray-600'} drop-shadow-sm text-left text-sm`}>
                    {t('common.noDescription')}
                  </p>
                )}
              </div>
              <div className={`flex-shrink-0 ${isDarkMode ? 'text-white/60' : 'text-gray-500'} transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </div>
            </button>
          </div>
          
          {isExpanded && (
            <div className="flex items-center justify-end flex-shrink-0 gap-3 ml-3">
              <BanfTypeSelector
                selectedType={selectedType}
                onTypeChange={onTypeChange}
              />
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {/* Favorite Button */}
                {onAddToFavorite && (
                  <button
                    onClick={() => onAddToFavorite(item)}
                    title={t('cart.addToFavorites')}
                    className={`rounded-2xl transition-colors ${isDarkMode ? 'bg-amber-600/20 hover:bg-amber-600/40 text-amber-300' : 'bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-200'}`}
                  >
                    <div className="px-3 py-2 flex items-center">
                      <Star className="w-4 h-4" />
                    </div>
                  </button>
                )}
                {/* Copy Button */}
                <button
                  onClick={() => onCopy(item.id)}
                  className={`relative group/copy overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(29,78,216,0.6) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                  title={t('banf.item.copyPosition')}
                >
                  {/* Smooth Hover Overlay - Dark only */}
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/copy:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    } : undefined}
                  ></div>

                  <div className="relative z-10 px-3 py-2 flex items-center font-medium text-white transition-all duration-300">
                    <Copy className="w-4 h-4 mr-1.5 transition-transform duration-300 group-hover/copy:scale-110" />
                    {t('banf.item.copy')}
                  </div>

                  {/* Wave Effect - Dark only */}
                  <div className={`absolute inset-0 opacity-0 group-hover/copy:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/copy:translate-x-full transition-transform duration-800"
                      style={isDarkMode ? {
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                      } : undefined}
                    ></div>
                  </div>
                </button>

              {/* Remove Button */}
              <button
              onClick={() => onRemove(item.id)}
              disabled={!canRemove}
              className={`relative group/remove overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${isDarkMode ? '' : 'bg-red-600 hover:bg-red-700'}`}
              title={t('banf.item.removePosition')}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(220,38,38,0.7) 0%, rgba(185,28,28,0.7) 50%, rgba(153,27,27,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : undefined}
            >
              {/* Smooth Hover Overlay - Dark only */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/remove:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : undefined}
              ></div>

              <div className="relative z-10 px-3 py-2 flex items-center font-medium text-white transition-all duration-300">
                <Trash2 className="w-4 h-4 mr-1.5 transition-transform duration-300 group-hover/remove:scale-110" />
                {t('banf.item.remove')}
              </div>

              {/* Wave Effect - Dark only */}
              <div className={`absolute inset-0 opacity-0 group-hover/remove:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/remove:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : undefined}
                ></div>
              </div>
            </button>
              </div>
            </div>
          )}
        </div>

        {/* Validation Error Badge on Collapsed Item */}
        {!isExpanded && validationErrors && validationErrors.length > 0 && (
          <div className={`mt-2 p-3 rounded-2xl ${isDarkMode ? 'bg-red-900/20 border border-red-400/30' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'} flex items-center`}>
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              {t('banf.item.missingFields')}{validationErrors.join(', ')}
            </p>
          </div>
        )}

        {/* Collapsed Summary */}
        {!isExpanded && (item.description || item.quantity || item.price) && (
          <div className={`mt-3 p-3 rounded-2xl ${isDarkMode ? 'bg-gradient-to-r from-white/10 via-white/5 to-white/10 backdrop-blur-xl border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  <span className="font-medium">{t('banf.item.article')}</span> {item.description || t('banf.item.naFallback')}
                </div>
                {item.quantity && (
                  <div className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                    <span className="font-medium">{t('banf.item.quantity')}</span> {formatQuantity(item.quantity, item.unit)} {item.unit}
                  </div>
                )}
                {(item.quantity || item.price || (item.services && item.services.length > 0)) && (
                  <div className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                    <span className="font-medium">{t('banf.item.positionValue')}</span> {formatGermanPrice(calculatePositionValue())}
                  </div>
                )}
              </div>
              {item.price > 0 && (
                <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {formatGermanPrice(parseFloat(item.price || 0) * parseFloat(item.quantity || 0))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Collapsible Content */}
        <div className={`transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          {/* Tab Navigation */}
          <div className="mb-6">
            {/* Tab Header - Flat Pill Design */}
            <div className={`flex space-x-1 mb-6 p-1 rounded-2xl ${isDarkMode ? 'bg-gray-800/30 border border-gray-700/30 backdrop-blur-xl' : 'bg-slate-100 border border-slate-200'}`}>
              <button
                onClick={() => onTabChange('allgemein')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'allgemein'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'allgemein' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'dienstleistung'
                        ? (isDarkMode ? '#f97316' : '#ea580c')
                        : selectedType === 'psp'
                        ? (isDarkMode ? '#f59e0b' : '#d97706')
                        : (isDarkMode ? '#3b82f6' : '#2563eb'))
                    : 'transparent'
                }}
              >
                <Settings className="w-4 h-4" />
                <span>{t('banf.item.tabGeneral')}</span>
              </button>
              
              {/* Limits Tab - immer anzeigen */}
              <button
                onClick={() => onTabChange('limits')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'limits'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'limits' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'material'
                        ? (isDarkMode ? '#3b82f6' : '#2563eb')
                        : (isDarkMode ? '#f59e0b' : '#d97706'))
                    : 'transparent'
                }}
              >
                <Activity className="w-4 h-4" />
                <span>{t('banf.item.tabLimits')}</span>
              </button>
              
              {/* Leistungen Tab - immer anzeigen */}
              <button
                onClick={() => onTabChange('leistungen')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'leistungen'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'leistungen' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'material'
                        ? (isDarkMode ? '#3b82f6' : '#2563eb')
                        : (isDarkMode ? '#06b6d4' : '#0891b2'))
                    : 'transparent'
                }}
              >
                <Briefcase className="w-4 h-4" />
                <span>{t('banf.item.tabServices')}</span>
              </button>
              
              <button
                onClick={() => onTabChange('bezugsquelle')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'bezugsquelle'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'bezugsquelle' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'material'
                        ? (isDarkMode ? '#3b82f6' : '#2563eb')
                        : selectedType === 'dienstleistung'
                        ? (isDarkMode ? '#f97316' : '#ea580c')
                        : (isDarkMode ? '#8b5cf6' : '#7c3aed'))
                    : 'transparent'
                }}
              >
                <ShoppingBag className="w-4 h-4" />
                <span>{t('banf.item.tabSource')}</span>
              </button>
              
              <button
                onClick={() => onTabChange('texte')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'texte'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'texte' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'material'
                        ? (isDarkMode ? '#3b82f6' : '#2563eb')
                        : selectedType === 'dienstleistung'
                        ? (isDarkMode ? '#f97316' : '#ea580c')
                        : (isDarkMode ? '#8b5cf6' : '#7c3aed'))
                    : 'transparent'
                }}
              >
                <Type className="w-4 h-4" />
                <span>{t('banf.item.tabTexts')}</span>
              </button>
              
              <button
                onClick={() => onTabChange('anhaenge')}
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'anhaenge'
                    ? `text-white shadow-sm`
                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'} hover:bg-gray-100 dark:hover:bg-gray-800/50`
                }`}
                style={{
                  backgroundColor: activeTab === 'anhaenge' 
                    ? (selectedType === 'kostenstelle' 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : selectedType === 'auftrag'
                        ? (isDarkMode ? '#9333ea' : '#7e22ce')
                        : selectedType === 'material'
                        ? (isDarkMode ? '#3b82f6' : '#2563eb')
                        : selectedType === 'dienstleistung'
                        ? (isDarkMode ? '#f97316' : '#ea580c')
                        : (isDarkMode ? '#8b5cf6' : '#7c3aed'))
                    : 'transparent'
                }}
              >
                <Paperclip className="w-4 h-4" />
                <span>{t('banf.item.tabAttachments')}</span>
              </button>
            </div>
            
            {/* Tab Content Background */}
            <div className={`mt-4 p-6 rounded-2xl ${isDarkMode ? 'bg-white/5 backdrop-blur-xl border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
              {/* Tab content will be rendered inside this container */}

          {/* Tab Content */}
          {activeTab === 'allgemein' && (
            <GeneralTab
              item={item}
              onUpdate={onUpdate}
              themeColors={themeColors}
              units={units}
              hasLimitsData={hasLimitsData}
              hasServices={hasServices}
              shouldHideKostenstelle={shouldHideKostenstelle}
              calculateEffectivePrice={calculateEffectivePrice}
              costCenterName={costCenterName}
              plantName={plantName}
              purchasingOrgName={purchasingOrgName}
              purchasingGroupName={purchasingGroupName}
              user={user}
              validationErrors={validationErrors}
            />
          )}

          {/* Limits Tab Content - immer anzeigen */}
          {activeTab === 'limits' && (
            <LimitsTab
              item={item}
              onUpdate={onUpdate}
              themeColors={themeColors}
            />
          )}

          {/* Leistungen Tab Content - immer anzeigen */}
          {activeTab === 'leistungen' && (
            <ServicesTab
              item={item}
              onUpdate={onUpdate}
              selectedType={selectedType}
              units={units}
              themeColors={themeColors}
            />
          )}

          {/* Texte Tab Content */}
          {activeTab === 'texte' && (
            <TextsTab
              item={item}
              onUpdate={onUpdate}
              themeColors={themeColors}
            />
          )}

          {/* Anhänge Tab Content */}
          {activeTab === 'anhaenge' && (
            <AttachmentsTab
              item={item}
              onUpdate={onUpdate}
            />
          )}
          {/* Bezugsquelle Tab Content */}
          {activeTab === 'bezugsquelle' && (
            <SourceTab
              item={item}
              onUpdate={onUpdate}
              themeColors={themeColors}
              user={user}
            />
          )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BanfItemCard;